import Box from "@mui/material/Box";
import type { ReactNode } from "react";

import type { Band, CourtConflictView, TimeCheckResult } from "@/libs/tournamentSetupApi";

import { TermHelp } from "./term-help";
import { bandText, BandBar, CheckIcon, DenseButton, Mono, SetupCard, tone, WarningIcon } from "./ui";

const ORDER: Band[] = ["plenty", "tight", "wont_fit"];

export function worstBand(result: TimeCheckResult): Band {
  return result.days.reduce<Band>((worst, day) => (ORDER.indexOf(day.band) > ORDER.indexOf(worst) ? day.band : worst), "plenty");
}

function ConflictsLine({ conflicts }: { conflicts: CourtConflictView[] }) {
  if (!conflicts.length) return null;
  return (
    <Box sx={{ fontSize: 12, color: tone.warnNoteText, lineHeight: 1.4 }}>
      <b>
        {conflicts.length} existing booking{conflicts.length === 1 ? "" : "s"} stay
      </b>{" "}
      and aren’t counted: {conflicts.slice(0, 3).map((c) => c.label).join("; ")}
      {conflicts.length > 3 ? `; and ${conflicts.length - 3} more` : ""}.
    </Box>
  );
}

/** The time check from the server: per-day court-hours, bands and the rest check (F1, F3). */
export function TimeCheckCard({
  result,
  onEditAssumptions,
  compactUnits,
  conflicts = [],
  footer,
}: {
  result: TimeCheckResult;
  onEditAssumptions?: () => void;
  compactUnits?: boolean;
  conflicts?: CourtConflictView[];
  footer?: ReactNode;
}) {
  const worst = worstBand(result);
  const rest = result.rest_summary;
  return (
    <SetupCard
      actions={
        <Box component="span" sx={{ fontWeight: 600, fontSize: 12, color: result.all_fit ? tone.brandText : bandText[worst] }}>
          {result.headline}
        </Box>
      }
      title={
        <>
          Time check <TermHelp label="How the time check works" term="court_hours" />
        </>
      }
    >
      <Box sx={{ p: "12px 14px", display: "flex", flexDirection: "column", gap: "10px" }}>
        {result.days.map((day) => (
          <Box key={day.play_date}>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, gap: 1 }}>
              <b>{day.title}</b>
              <Mono sx={{ fontWeight: 700, color: bandText[day.band] }}>
                {day.used_court_hours} of {day.capacity_court_hours}
                {compactUnits ? "" : " court-hours"} · {day.percent_label}%
              </Mono>
            </Box>
            <BandBar band={day.band} percent={day.percent} />
            <Box sx={{ fontSize: 11.5, mt: "3px", color: bandText[day.band] }}>
              <b>{day.band_long_label}.</b> {day.spare_label}
            </Box>
          </Box>
        ))}
        {rest ? (
          <Box sx={{ fontSize: 12, color: rest.fits ? tone.ink2 : tone.badNoteText, lineHeight: 1.4, display: "flex", gap: "6px", alignItems: "flex-start" }}>
            <Box component="span" sx={{ flex: "none", mt: "1px", color: rest.fits ? tone.brandText : tone.badText }}>
              {rest.fits ? <CheckIcon size={14} /> : <WarningIcon />}
            </Box>
            <span>
              <b>Rest check:</b> {rest.sentence}
            </span>
          </Box>
        ) : null}
        <ConflictsLine conflicts={conflicts} />
        <Box sx={{ fontSize: 11.5, color: tone.subtle, lineHeight: 1.45, borderTop: `1px solid ${tone.divider}`, pt: 1 }}>
          {result.assumptions_line}
          {onEditAssumptions ? (
            <>
              {" · "}
              <DenseButton onClick={onEditAssumptions} sx={{ fontSize: 11.5 }} variant="link">
                Edit
              </DenseButton>
            </>
          ) : null}
          <br />
          {result.bands_line}
        </Box>
        {footer}
      </Box>
    </SetupCard>
  );
}

/** The phone row (F24): one line, tap for details. */
export function TimeCheckRow({ result, onOpen }: { result: TimeCheckResult; onOpen?: () => void }) {
  const worst = worstBand(result);
  const colors = {
    plenty: { border: tone.brandNoteLine, bg: tone.brandNote, fg: tone.brandNoteText },
    tight: { border: tone.warnLine, bg: tone.warnSoft, fg: tone.warnNoteText },
    wont_fit: { border: tone.badLine, bg: tone.badSoft, fg: tone.badNoteText },
  }[worst];
  const word = { plenty: "Plenty", tight: "Tight", wont_fit: "Won’t fit" }[worst];
  return (
    <Box
      component="button"
      onClick={onOpen}
      sx={{
        height: 44,
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: "12px",
        borderRadius: "10px",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.bg,
        color: colors.fg,
        font: "inherit",
        fontWeight: 600,
        fontSize: 13.5,
        cursor: "pointer",
        textAlign: "left",
      }}
      type="button"
    >
      {worst === "plenty" ? <CheckIcon color={tone.brandText} size={16} /> : <WarningIcon />}
      <span>
        Time check: {result.days.map((day) => `${day.label} ${day.percent_label}%`).join(" · ")} · {word}
      </span>
      <Box sx={{ flexGrow: 1 }} />
      <span aria-hidden>›</span>
    </Box>
  );
}
